/**
 * Riga di un test della libreria, come quella delle schede: il riquadro con
 * l'icona del metodo (o dei più metodi, se le misure ne usano più d'uno), il
 * nome con l'unità, le misure se sono più d'una, il metodo, RPE e minuti se
 * ci sono, le esecuzioni e gli attributi valutati con l'icona della famiglia.
 * Chi scrive esegue un test attivo senza aprirlo, dal pulsante a destra.
 */

import { Link } from 'react-router-dom';
import { GaugeIcon, HistoryIcon, PlayIcon } from 'lucide-react';
import { ETICHETTA_METODO_TEST, FAMIGLIE_ATTRIBUTO } from '@calcio/shared';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { ComandoIcona } from '../comuni/Azioni.js';
import { dataBreve } from '../comuni/dati.js';
import { IconaFamiglia } from '../comuni/famiglie.js';
import { IconaMetodi } from './icone.js';
import { metodiDelTest, type TestInElenco } from './test-misura.js';

export function RigaTest({
  test: t,
  ss,
  esegue,
}: {
  test: TestInElenco;
  ss: string;
  esegue: boolean;
}) {
  const percorso = `/sq/${ss}/test/${t.id}`;
  const metodi = metodiDelTest(t);
  const [sola] = t.misure.length === 1 ? t.misure : [];
  // Gli attributi di tutte le misure in ordine di famiglia, come nel resto dell'app.
  const attributi = t.misure
    .flatMap((m) => m.attributi)
    .sort(
      (a, b) => FAMIGLIE_ATTRIBUTO.indexOf(a.famiglia) - FAMIGLIE_ATTRIBUTO.indexOf(b.famiglia),
    );
  return (
    // Il link copre tutta la riga; il pulsante Esegui gli sta sopra.
    <div className="relative flex items-center gap-3 rounded-lg border border-border bg-card p-2.5">
      <span
        className={cn(
          'flex size-12 shrink-0 items-center justify-center rounded-md border border-border bg-background',
          !t.attivo && 'opacity-60',
        )}
      >
        <IconaMetodi metodi={metodi} className="size-5" />
      </span>

      <span className="flex min-w-0 flex-1 flex-col gap-1">
        <span className="flex flex-wrap items-center gap-2">
          <Link
            to={percorso}
            className={cn(
              'text-base font-semibold break-words after:absolute after:inset-0 after:rounded-lg',
              !t.attivo && 'text-muted-foreground',
            )}
          >
            {t.nome}
          </Link>
          {sola?.unita && <span className="text-sm text-muted-foreground">{sola.unita}</span>}
          {!t.attivo && <Badge variant="outline">Disattivato</Badge>}
        </span>
        {!sola && (
          <span className="text-sm">
            {t.misure.map((m, i) => (
              <span key={m.id}>
                {i > 0 && <span className="text-muted-foreground"> · </span>}
                {m.nome}
                {m.unita && <span className="text-muted-foreground"> {m.unita}</span>}
              </span>
            ))}
          </span>
        )}
        <span className="flex flex-col gap-1 text-sm @md:flex-row @md:items-center @md:justify-between @md:gap-3">
          <span className="flex flex-wrap items-center gap-x-3 gap-y-1 text-muted-foreground">
            <span>{metodi.map((m) => ETICHETTA_METODO_TEST[m]).join(', ')}</span>
            {t.rpeRiferimento !== null && (
              <span className="flex items-center gap-1" title="Carico di riferimento in seduta">
                <GaugeIcon aria-hidden className="size-4" />
                <span className="tabular-nums">
                  RPE {t.rpeRiferimento} · {t.minutiRiferimento} min
                </span>
              </span>
            )}
            <span
              className="flex items-center gap-1"
              title="Esecuzioni in tutte le squadre della società"
            >
              <HistoryIcon aria-hidden className="size-4" />
              <span className="font-medium text-foreground tabular-nums">{t.esecuzioni}</span>
              {t.ultimaEsecuzione && (
                <span className="tabular-nums">· ultima {dataBreve(t.ultimaEsecuzione)}</span>
              )}
            </span>
          </span>
          <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
            {attributi.map((a) => (
              <span key={a.id} className="flex items-center gap-1">
                <IconaFamiglia famiglia={a.famiglia} className="size-4 text-muted-foreground" />
                <span className="font-medium">{a.nome}</span>
              </span>
            ))}
          </span>
        </span>
      </span>

      {esegue && t.attivo && (
        <ComandoIcona
          etichetta={`Esegui ${t.nome}`}
          Icona={PlayIcon}
          size="icon-sm"
          a={`${percorso}/esegui`}
          className="relative z-10 inline-flex shrink-0"
        />
      )}
    </div>
  );
}
